import { Application, Container, Graphics, Rectangle, type Renderer, Sprite, Texture } from 'pixi.js';
import { Tile, TILE_SIZE, tileAt, type TileMap } from '../world/tilemap';
import type { MapDef } from '../world/maps/valleDelleFaglie';
import { Camera } from './camera';
import { LightingSystem } from './lighting';
import { loadGameAssets, type GameAssets, type PropId } from './assets';
import { allGroundIds, groundTileId } from './groundArt';
import { drawPlayerHD, type Direction } from './spriteFactory';

/**
 * Scena PixiJS con gli asset CraftPix (terreno 256px autotilato, props
 * hand-drawn) renderizzati in world-space a 32px/tile:
 *
 *   stage
 *   ├─ worldRoot   (trasformata camera)
 *   │   ├─ groundLayer  (terreno autotilato + glow anomalie — cacheAsTexture)
 *   │   ├─ waterLayer   (lago cartoon con riflessi animati)
 *   │   └─ entityLayer  (alberi, rocce, casa, torre, giocatore — y-sorted)
 *   ├─ ambient     (overlay multiply per il ciclo giorno/notte)
 *   └─ lightRoot   (luci additive sopra l'ambiente)
 */

const GROUND_SRC = 256; // lato dei tile sorgente

/** Larghezza in tile-mondo dei props (l'altezza segue le proporzioni). */
const PROP_WIDTH: Partial<Record<PropId, number>> = {
  'tree-small': 1.7,
  'tree-medium': 2.1,
  'tree-large': 2.5,
  'bush-small': 0.85,
  'bush-medium': 1.05,
  'bush-large': 1.5,
  'rock-1': 0.95,
  'rock-2': 0.95,
  'rock-3': 0.9,
  'rock-4': 0.9,
  'rock-5': 0.9,
  house: 3.1,
  'magic-tower': 1.9,
  well: 1.4,
};

interface Sparkle {
  gfx: Graphics;
  phase: number;
}

/**
 * Texture radiale per glow e luci, generata dal renderer (cerchi concentrici
 * con alpha cumulativa): evita l'upload di canvas 2D con gradienti, che su
 * alcuni driver WebGL arriva opaco.
 */
function makeGlowTexture(renderer: Renderer, radius = 64, steps = 28): Texture {
  const gfx = new Graphics();
  for (let i = steps; i >= 1; i--) {
    const t = i / steps;
    gfx.circle(radius, radius, radius * t).fill({ color: 0xffffff, alpha: 0.075 * (1 - t) + 0.012 });
  }
  return renderer.generateTexture({
    target: gfx,
    frame: new Rectangle(0, 0, radius * 2, radius * 2),
  });
}

export class GameRenderer {
  readonly app: Application;
  readonly camera: Camera;
  readonly lighting: LightingSystem;

  private assets!: GameAssets;
  private worldRoot = new Container();
  private groundLayer = new Container();
  private decalLayer = new Container(); // glow e segni a terra (non cached)
  private waterLayer = new Container();
  private entityLayer = new Container();
  private lightRoot = new Container();

  private playerSprite!: Sprite;
  private playerLight!: ReturnType<LightingSystem['addLight']>;
  private playerFrames = new Map<string, Texture>();
  private sparkles: Sparkle[] = [];
  private glowTexture: Texture;
  private elapsed = 0;

  private constructor(app: Application, assets: GameAssets, mapDef: MapDef) {
    this.app = app;
    this.assets = assets;
    const worldW = mapDef.map.width * TILE_SIZE;
    const worldH = mapDef.map.height * TILE_SIZE;
    this.camera = new Camera(app.screen.width, app.screen.height, worldW, worldH);
    this.camera.resize(app.screen.width, app.screen.height);

    this.entityLayer.sortableChildren = true;
    this.worldRoot.addChild(this.groundLayer, this.decalLayer, this.waterLayer, this.entityLayer);

    this.glowTexture = makeGlowTexture(app.renderer);
    this.lighting = new LightingSystem(app.screen.width, app.screen.height, this.glowTexture);
    this.lightRoot.addChild(this.lighting.lightLayer);

    app.stage.addChild(this.worldRoot, this.lighting.ambientOverlay, this.lightRoot);

    this.buildGround(mapDef.map);
    this.buildWater(mapDef.map);
    this.buildProps(mapDef);
    this.buildPlayer();
  }

  static async create(host: HTMLElement, mapDef: MapDef): Promise<GameRenderer> {
    const app = new Application();
    await app.init({
      resizeTo: host,
      background: '#79c04d',
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      preference: 'webgl',
    });
    host.appendChild(app.canvas);
    const assets = await loadGameAssets(allGroundIds());
    const renderer = new GameRenderer(app, assets, mapDef);
    app.renderer.on('resize', () => renderer.onResize());
    return renderer;
  }

  // ——— Terreno ———

  private buildGround(map: TileMap): void {
    for (let ty = 0; ty < map.height; ty++) {
      for (let tx = 0; tx < map.width; tx++) {
        const id = groundTileId(map, tx, ty);
        const sprite = new Sprite(this.assets.ground.get(id)!);
        sprite.position.set(tx * TILE_SIZE, ty * TILE_SIZE);
        sprite.scale.set(TILE_SIZE / GROUND_SRC);
        this.groundLayer.addChild(sprite);

        if (tileAt(map, tx, ty) === Tile.Anomaly) {
          // alone viola sopra il terreno + luce dinamica notturna
          const cx = tx * TILE_SIZE + TILE_SIZE / 2;
          const cy = ty * TILE_SIZE + TILE_SIZE / 2;
          const glow = new Graphics();
          for (let r = 8; r >= 1; r--) {
            glow.circle(cx, cy, (TILE_SIZE * 0.95 * r) / 8).fill({
              color: 0xb178e8,
              alpha: 0.075,
            });
          }
          glow.circle(cx, cy, TILE_SIZE * 0.16).fill({ color: 0xf2e6ff, alpha: 0.85 });
          this.decalLayer.addChild(glow);
          this.lighting.addLight(cx, cy, 40, 0xc9a8ff, 0.35);
        }
      }
    }
    // statico: rasterizza una sola volta (resolution 2 per non perdere dettaglio allo zoom)
    this.groundLayer.cacheAsTexture({ resolution: 2, antialias: true });
  }

  // ——— Acqua cartoon ———

  private buildWater(map: TileMap): void {
    const isWater = (tx: number, ty: number) => tileAt(map, tx, ty) === Tile.Water;

    // Bounding box dello specchio d'acqua: il lago è disegnato come un'unica
    // forma arrotondata in stile cartoon (riva chiara + fondale), la spiaggia
    // attorno arriva dall'autotiling della sabbia.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let found = false;
    for (let ty = 0; ty < map.height; ty++) {
      for (let tx = 0; tx < map.width; tx++) {
        if (!isWater(tx, ty)) continue;
        found = true;
        minX = Math.min(minX, tx);
        minY = Math.min(minY, ty);
        maxX = Math.max(maxX, tx);
        maxY = Math.max(maxY, ty);
      }
    }
    if (!found) return;

    const x = minX * TILE_SIZE - 6;
    const y = minY * TILE_SIZE - 6;
    const w = (maxX - minX + 1) * TILE_SIZE + 12;
    const h = (maxY - minY + 1) * TILE_SIZE + 12;
    const lake = new Graphics();
    lake.roundRect(x, y, w, h, 26).fill(0x8fd4ef); // riva chiara
    lake.roundRect(x + 7, y + 7, w - 14, h - 14, 20).fill(0x4aa6d8); // acqua
    lake.roundRect(x + 7, y + h * 0.45, w - 14, h * 0.55 - 7, 20).fill(0x3f95c7); // fondale
    this.waterLayer.addChild(lake);

    // riflessi animati: trattini bianchi che pulsano
    let i = 0;
    for (let ty = 0; ty < map.height; ty++) {
      for (let tx = 0; tx < map.width; tx++) {
        if (!isWater(tx, ty)) continue;
        if ((tx * 7 + ty * 11) % 3 !== 0) continue;
        const gfx = new Graphics();
        const w = 10 + ((tx + ty) % 3) * 4;
        gfx.roundRect(0, 0, w, 3.5, 2).fill(0xe8f7ff);
        gfx.position.set(
          tx * TILE_SIZE + 6 + ((tx * 13) % 12),
          ty * TILE_SIZE + 8 + ((ty * 17) % 14),
        );
        this.waterLayer.addChild(gfx);
        this.sparkles.push({ gfx, phase: (i++ * 1.7) % (Math.PI * 2) });
      }
    }
  }

  // ——— Props ———

  private propSprite(id: PropId, tx: number, ty: number, jitterX = 0): Sprite {
    const texture = this.assets.props.get(id)!;
    const sprite = new Sprite(texture);
    const widthTiles = PROP_WIDTH[id] ?? 1;
    sprite.scale.set((widthTiles * TILE_SIZE) / texture.width);
    sprite.anchor.set(0.5, 0.94);
    const x = tx * TILE_SIZE + TILE_SIZE / 2 + jitterX;
    const y = (ty + 1) * TILE_SIZE;
    sprite.position.set(x, y);
    sprite.zIndex = y;
    this.entityLayer.addChild(sprite);
    return sprite;
  }

  private buildProps(mapDef: MapDef): void {
    const map = mapDef.map;
    const landmark = new Map<string, PropId>();
    for (const mark of mapDef.landmarks ?? []) {
      landmark.set(`${mark.tx},${mark.ty}`, mark.prop as PropId);
    }

    for (let ty = 0; ty < map.height; ty++) {
      for (let tx = 0; tx < map.width; tx++) {
        const tile = map.tiles[ty * map.width + tx]!;
        const seed = tx * 31 + ty * 17;
        const special = landmark.get(`${tx},${ty}`);
        if (special) {
          this.propSprite(special, tx, ty);
          if (special === 'magic-tower') {
            this.lighting.addLight(
              tx * TILE_SIZE + TILE_SIZE / 2,
              ty * TILE_SIZE - TILE_SIZE / 2,
              72,
              0x9ad2ff,
              0.8,
            );
          }
          continue;
        }

        switch (tile) {
          case Tile.Tree: {
            const border = tx === 0 || ty === 0 || tx === map.width - 1 || ty === map.height - 1;
            const variant = border
              ? 'tree-medium'
              : (['tree-small', 'tree-medium', 'tree-large'] as const)[seed % 3]!;
            this.propSprite(variant, tx, ty, border ? 0 : (seed % 9) - 4);
            break;
          }
          case Tile.Rock:
            this.propSprite(
              (['rock-1', 'rock-2', 'rock-3', 'rock-4', 'rock-5'] as const)[seed % 5]!,
              tx,
              ty,
            );
            break;
          case Tile.TallGrass:
            // cespugli = zona incontri: i prop bassi non bloccano la vista
            this.propSprite(seed % 4 === 0 ? 'bush-medium' : 'bush-small', tx, ty, (seed % 7) - 3)
              .zIndex -= TILE_SIZE; // restano sotto il giocatore
            break;
          case Tile.Flower:
            this.propSprite('bush-large', tx, ty).zIndex -= TILE_SIZE;
            break;
          case Tile.HealStation: {
            // la porta è il tile H: la casa (3 tile di base) si disegna qui sopra
            this.propSprite('house', tx, ty);
            this.lighting.addLight(
              tx * TILE_SIZE + TILE_SIZE / 2,
              ty * TILE_SIZE,
              72,
              0xffe9b0,
              0.7,
            );
            break;
          }
          default:
            break;
        }
      }
    }
  }

  // ——— Giocatore ———

  private playerFrame(dir: Direction, step: number): Texture {
    const key = `${dir}:${step}`;
    let frame = this.playerFrames.get(key);
    if (!frame) {
      frame = Texture.from(drawPlayerHD(dir, step));
      this.playerFrames.set(key, frame);
    }
    return frame;
  }

  private buildPlayer(): void {
    this.playerSprite = new Sprite(this.playerFrame('giu', 0));
    this.playerSprite.scale.set(0.52);
    this.playerSprite.anchor.set(0.5, 0.95);
    this.entityLayer.addChild(this.playerSprite);
    this.playerLight = this.lighting.addLight(0, 0, 80, 0xfff2cc, 0.85);
  }

  updatePlayer(x: number, y: number, dir: Direction, walking: boolean): void {
    const step = walking ? Math.floor(this.elapsed * 7) % 4 : 0;
    this.playerSprite.texture = this.playerFrame(dir, step);
    this.playerSprite.position.set(x, y);
    this.playerSprite.zIndex = y;
    this.playerLight.sprite.position.set(x, y - 10);
  }

  render(dt: number, timeOfDay: number, focusX: number, focusY: number): void {
    this.elapsed += dt;
    this.camera.follow(focusX, focusY, dt);
    const t = this.camera.transform();
    this.worldRoot.position.set(t.x, t.y);
    this.worldRoot.scale.set(t.scale);
    this.lightRoot.position.set(t.x, t.y);
    this.lightRoot.scale.set(t.scale);
    this.lighting.update(timeOfDay, this.elapsed);

    for (const sparkle of this.sparkles) {
      sparkle.gfx.alpha = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(this.elapsed * 1.6 + sparkle.phase));
    }
  }

  private onResize(): void {
    this.camera.resize(this.app.screen.width, this.app.screen.height);
    this.lighting.resize(this.app.screen.width, this.app.screen.height);
  }
}
